import React from 'react';

/**
 * The classic Indian-menu dietary marker (FSSAI-style): a small bordered
 * square with a filled dot — green = veg, red/brown = non-veg — plus a leaf
 * for vegan. Pure CSS (no emoji) so it renders crisp at any size and matches
 * across platforms.
 *
 * Renders nothing when the item is undeclared (null/undefined/NONE) — menus
 * that don't use dietary markers see no change.
 */

export type DietaryTypeValue = 'VEG' | 'NON_VEG' | 'VEGAN' | 'NONE' | null | undefined;

export const DIETARY_LABELS: Record<string, string> = {
  VEG: 'Vegetarian',
  NON_VEG: 'Non-vegetarian',
  VEGAN: 'Vegan',
};

const COLORS: Record<string, string> = {
  VEG: '#1E7E34', // deep green
  NON_VEG: '#A02C2C', // deep red-brown (calmer than pure red, still unmistakable)
  VEGAN: '#1E7E34',
};

interface DietaryDotProps {
  type: DietaryTypeValue;
  /** sm = inline beside names (14px), md = forms/legend (18px) */
  size?: 'sm' | 'md';
  /** Show the text label beside the marker (legend / forms). */
  withLabel?: boolean;
}

export const DietaryDot: React.FC<DietaryDotProps> = ({ type, size = 'sm', withLabel = false }) => {
  if (!type || type === 'NONE') return null;
  const color = COLORS[type];
  if (!color) return null;

  const px = size === 'sm' ? 14 : 18;
  const dotPx = size === 'sm' ? 7 : 9;

  return (
    <span
      role="img"
      aria-label={DIETARY_LABELS[type]}
      title={DIETARY_LABELS[type]}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: withLabel ? 6 : 0,
        flexShrink: 0,
        verticalAlign: 'middle',
        // Breathing room when the marker sits inline before a dish name; label
        // mode manages its own spacing via the flex gap.
        marginRight: withLabel ? 0 : 6,
      }}
    >
      <span
        style={{
          width: px,
          height: px,
          border: `1.5px solid ${color}`,
          borderRadius: 3,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fff',
          boxSizing: 'border-box',
        }}
      >
        {type === 'VEGAN' ? (
          // Tiny leaf: a filled circle with a leaf-like notch reads muddy at 14px,
          // so use a simple sprout glyph scaled to the box instead.
          <svg viewBox="0 0 24 24" width={px - 5} height={px - 5} fill={color} aria-hidden="true">
            <path d="M12 22c0-6 1-10 8-14-1 8-3 12-8 14zM12 22c0-5-1-8-7-11 1 6 3 9 7 11z" />
          </svg>
        ) : (
          <span
            style={{
              width: dotPx,
              height: dotPx,
              borderRadius: '50%',
              backgroundColor: color,
            }}
          />
        )}
      </span>
      {withLabel && (
        <span style={{ fontSize: size === 'sm' ? '0.75rem' : '0.8125rem', color: 'var(--color-text-primary, #333)', fontWeight: 500 }}>
          {DIETARY_LABELS[type]}
        </span>
      )}
    </span>
  );
};

export default DietaryDot;
